import React, { useEffect, useState, useRef } from 'react';
import { Header } from './components/Header';
import { SearchBar } from './components/SearchBar';
import { ExportButton } from './components/ExportButton';
import { DarkModeToggle } from './components/DarkModeToggle';
import { KeyboardShortcuts } from './components/KeyboardShortcuts';
import { VisionSection } from './components/VisionSection';
import { ResearchSection } from './components/ResearchSection';
import { FeaturesSection } from './components/FeaturesSection';
import { TimelineSection } from './components/TimelineSection';
import { LaunchSection } from './components/LaunchSection';
import { Footer } from './components/Footer';
import { PasswordModal } from './components/PasswordModal';
import { EditIcon, SaveIcon } from 'lucide-react';
export function App() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('heliona-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [roadmapData, setRoadmapData] = useState(() => {
    const saved = localStorage.getItem('heliona-roadmap');
    return saved ? JSON.parse(saved) : {
      vision: {
        title: 'Heliona is a mental wellness workspace for people leaders.',
        description: 'We help organizations retain top talent, reduce turnover, and strengthen culture by equipping their leaders with wellness tools, continuous learning, and AI-powered insights — all supported by seamless access to coaches and therapists.'
      },
      research: {
        targetAudience: ['People Leaders (Executives, VPs, Directors, Managers)', 'HR & People Ops (CHROs, CPOs, CFOs)', 'Organizations (Mid-market & enterprise teams focused on retention and leadership health)'],
        competitors: [{
          name: 'Spring Health',
          description: 'Clinical-first, not leadership-driven'
        }, {
          name: 'BetterUp',
          description: 'Coaching model, lacks measurable analytics'
        }, {
          name: 'CultureAmp',
          description: 'Great data, lacks emotional depth'
        }, {
          name: 'Heliona',
          description: 'Combines leadership development, psychology, and AI in one workspace'
        }]
      },
      features: {
        leadership: [{
          name: 'Dashboard',
          desc: '4C overview + Helium Score'
        }, {
          name: 'Helium Score',
          desc: '4C Index'
        }, {
          name: 'Helium Care',
          desc: 'Coach/therapist matching'
        }, {
          name: 'Helium IQ',
          desc: 'AI-guided learning'
        }, {
          name: 'Helium Insight',
          desc: 'Sentiment analysis'
        }, {
          name: 'Culture Check',
          desc: 'Onboarding assessment'
        }, {
          name: 'Rewards / Events',
          desc: 'Certificates Tabs'
        }, {
          name: 'Conflict Management',
          desc: 'Live Training'
        }, {
          name: 'Hazel Workspace',
          desc: 'AI co-pilot'
        }],
        admin: [{
          name: 'Dashboard',
          desc: 'Executive overview'
        }, {
          name: 'Engagement Metrics',
          desc: 'Org Health'
        }, {
          name: 'ROI Calculator',
          desc: 'Value measurement'
        }, {
          name: 'Reports Hub',
          desc: 'Helium Wallet'
        }, {
          name: 'Manage Licenses',
          desc: 'Experiences Tab'
        }, {
          name: 'Hazel Workspace',
          desc: 'AI co-pilot assistant'
        }]
      },
      timeline: [{
        version: 'V1',
        title: 'People Leaders',
        period: 'Q1–Q2 2026',
        description: 'Launch Heliona platform with:',
        features: ['Culture Check', 'Helium Score', 'Helium Care', 'Helium IQ'],
        notes: ['Hazel Workspace (alpha)', '20 pilot companies (100–250 leaders)'],
        progress: 75
      }, {
        version: 'V2',
        title: 'Teams',
        period: 'Q3–Q4 2026',
        features: ['Add Rewards', 'Conflict Management', 'Live Training'],
        notes: ['Expand Insight analytics for team-level sentiment'],
        progress: 40
      }, {
        version: 'V3',
        title: 'Recruiting & Hiring',
        period: 'Q1–Q2 2027',
        features: ['Launch 4C Index™ for recruiting and culture-fit assessments', 'Introduce advanced analytics in Admin Portal'],
        notes: [],
        progress: 15
      }, {
        version: 'V4',
        title: 'Workforce Infrastructure',
        period: 'Q3–Q4 2027',
        features: ['Add HRIS, Payroll, Time Tracking', 'Launch ROI Simulator, Events & Certificates Tabs', 'Full Hazel integration across all portals'],
        notes: [],
        progress: 5
      }],
      launch: {
        metrics: ['Adoption Rate (% completing Culture Check within 30 days)', 'Engagement Rate (active leaders per week)', 'Retention Rate (renewal post-MVP)', 'ROI Improvement (turnover & burnout reduction)', 'NPS (leadership satisfaction)'],
        feedback: ['Executive feedback: ROI & usability', 'Manager feedback: engagement & usability', 'Coach/Therapist feedback: client alignment', 'Team feedback: sentiment & collaboration']
      }
    };
  });
  useEffect(() => {
    localStorage.setItem('heliona-roadmap', JSON.stringify(roadmapData));
  }, [roadmapData]);
  useEffect(() => {
    localStorage.setItem('heliona-dark-mode', JSON.stringify(isDarkMode));
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);
  const updateData = (section: string, data: any) => {
    setRoadmapData(prev => ({
      ...prev,
      [section]: data
    }));
  };
  const handleEditClick = () => {
    if (isEditMode) {
      setIsEditMode(false);
    } else {
      setShowPasswordModal(true);
    }
  };
  const handlePasswordSuccess = () => {
    setIsEditMode(true);
    setShowPasswordModal(false);
  };
  const handleSearchFocus = () => {
    searchInputRef.current?.focus();
  };
  const handleExportClick = () => {
    exportButtonRef.current?.click();
  };
  return <div className="min-h-screen bg-gradient-to-br from-[#E8FAF1] to-[#F5FFFA] dark:from-[#0A0F1A] dark:to-[#1A2332] text-[#1A2332] dark:text-white transition-colors duration-300" data-id="element-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12 pb-24" data-id="element-1">
        <Header isDarkMode={isDarkMode} data-id="element-2" />
        <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} ref={searchInputRef} data-id="element-3" />
        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 mb-8" data-id="element-4">
          <ExportButton ref={exportButtonRef} data-id="element-5" />
          <button onClick={handleEditClick} className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl ${isEditMode ? 'bg-[#7FE5A8] text-[#1A2332] hover:bg-[#6FD598]' : 'bg-[#1A2332] dark:bg-[#7FE5A8] text-white dark:text-[#1A2332] hover:bg-[#2A3F5F] dark:hover:bg-[#6FD598]'}`} data-id="element-6">
            {isEditMode ? <>
                <SaveIcon className="h-5 w-5" data-id="element-7" />
                Exit Edit Mode
              </> : <>
                <EditIcon className="h-5 w-5" data-id="element-8" />
                Edit Roadmap
              </>}
          </button>
        </div>
        <main className="mt-8 sm:mt-12" id="roadmap-content" data-id="element-9">
          <div className="space-y-6 sm:space-y-8" data-id="element-10">
            <VisionSection data={roadmapData.vision} isEditMode={isEditMode} onUpdate={data => updateData('vision', data)} searchQuery={searchQuery} data-id="element-11" />
            <ResearchSection data={roadmapData.research} isEditMode={isEditMode} onUpdate={data => updateData('research', data)} searchQuery={searchQuery} data-id="element-12" />
            <FeaturesSection data={roadmapData.features} isEditMode={isEditMode} onUpdate={data => updateData('features', data)} searchQuery={searchQuery} data-id="element-13" />
            <TimelineSection data={roadmapData.timeline} isEditMode={isEditMode} onUpdate={data => updateData('timeline', data)} searchQuery={searchQuery} data-id="element-14" />
            <LaunchSection data={roadmapData.launch} isEditMode={isEditMode} onUpdate={data => updateData('launch', data)} searchQuery={searchQuery} data-id="element-15" />
          </div>
        </main>
        <Footer data-id="element-16" />
      </div>
      <DarkModeToggle isDarkMode={isDarkMode} onToggle={() => setIsDarkMode(!isDarkMode)} data-id="element-17" />
      <KeyboardShortcuts onSearch={handleSearchFocus} onEdit={handleEditClick} onExport={handleExportClick} onDarkMode={() => setIsDarkMode(!isDarkMode)} data-id="element-18" />
      <PasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} onSuccess={handlePasswordSuccess} data-id="element-19" />
    </div>;
}